import { filter, find, includes, noop, reduce, some, stubArray, stubFalse } from 'lodash';
import React, { createContext, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { gql, useQuery } from 'urql';
import { ErrorLayout } from '.';
import { AppContextObject, AppContextProvider } from '../context/AppContext';
import { RoleName } from '../types';
import { HacpackQuery, Role, User } from '../types/graph';

type CurrentUserMembershipsRole = Role;

const currentUserQuery = gql<HacpackQuery>`
    query {
        currentUser {
            id
            login
            displayName
            memberships {
                group {
                    id
                    name
                    terminalNames
                    portCodes
                }
                roles {
                    id
                    name
                }
            }
            terminals {
                id
                name
                slug
                port
                portCode
                active
            }
        }
    }
`;

export type CurrentUserContextObject = {
    currentUser: User | null,
    reloadCurrentUser: () => void,
    fetchingCurrentUser: boolean,
    getRolesForTerminal: (terminalId: number) => Role[],
    hasRole: (name: RoleName) => boolean,
    hasRoleForTerminal: (name: RoleName, terminalId: number) => boolean,
    isSuperuser: boolean
};

export const CurrentUserContext = createContext<CurrentUserContextObject>({
    currentUser: null,
    reloadCurrentUser: noop,
    fetchingCurrentUser: false,
    getRolesForTerminal: stubArray,
    hasRole: stubFalse,
    hasRoleForTerminal: stubFalse,
    isSuperuser: false
});

type CurrentUserProps = PropsWithChildren<{
    onSignOut?: () => void
}>;



const CurrentUser = ({ children, onSignOut }: CurrentUserProps) => {
    const [result, reexecuteQuery] = useQuery<HacpackQuery>({
        query: currentUserQuery,
        requestPolicy: 'network-only'
    });

    const { data, fetching: fetchingCurrentUser, error } = result;

    const currentUser = useMemo(() => data?.currentUser ?? null, [data]);

    useEffect(() => {
        if (fetchingCurrentUser === false) {
            return;
        }

        const handle = setTimeout(reexecuteQuery, 60000);

        return () => {
            clearTimeout(handle);
        };
    }, [fetchingCurrentUser, reexecuteQuery]);

    useEffect(() => {
        if (currentUser === null) { onSignOut?.() }
    }, [currentUser, onSignOut]);

    const reloadCurrentUser = useCallback(() => reexecuteQuery(), [reexecuteQuery]);

    const getRolesForTerminal = useCallback((id: number) => {
        if (currentUser === null) {
            return [];
        }

        const terminal = find(currentUser.terminals, ['id', id]);

        if (terminal === undefined || terminal === null) {
            return [];
        }

        const terminalName = terminal.name;
        const portCode = terminal.portCode;

        return reduce(currentUser.memberships, (roles, membership) => {
            const { group } = membership;
            if (group === null || group === undefined) {
                return roles;
            }

            const { terminalNames, portCodes } = group;

            const rolesToPush = (includes(terminalNames, terminalName) || includes(portCodes, portCode))
                ? membership.roles??[]
                : filter(membership.roles, ['name', RoleName.SUPERUSER])

            roles.push(...rolesToPush);

            return roles;
        }, [] as CurrentUserMembershipsRole[])
    }, [currentUser])

    const hasRole = useCallback((name:any) => some(currentUser?.memberships, (membership) => some(membership.roles, { name })), [currentUser]);

    const hasRoleForTerminal = useCallback((name:any, terminalId:any) => some(getRolesForTerminal(terminalId), { name }), [getRolesForTerminal]);

    const isSuperuser = useMemo(() => hasRole(RoleName.SUPERUSER), [hasRole]);

    const context = useMemo<Partial<AppContextObject>>(() => {
        const user = currentUser ?? (fetchingCurrentUser ? undefined : null);

        return {
            user,
            terminals: user?.terminals
        };
    }, [currentUser, fetchingCurrentUser]);

    if (error !== undefined) {
        console.error(error);
        return <ErrorLayout>
            <button onClick={reloadCurrentUser}>Riprova</button>
        </ErrorLayout>;
    }

    return <AppContextProvider value={context}>
        <CurrentUserContext.Provider value={{ currentUser, fetchingCurrentUser, reloadCurrentUser, getRolesForTerminal, hasRole, isSuperuser, hasRoleForTerminal }}>
            {children}
        </CurrentUserContext.Provider>
    </AppContextProvider>;
}

export default CurrentUser;
